import type { FC, ReactNode } from 'react'
import type { PhotoId } from '@/components/generals/photo'
import type { CompanyName } from '@/components/art/wordmark'

import {
    Check,
    FileText,
    Forward,
    Hash,
    Heart,
    PartyPopper,
    Reply,
    X
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { delay } from '@/lib/motion'
import { LogoMark } from '@/components/generals/logo'
import { Wordmark } from '@/components/art/wordmark'
import { Photo } from '@/components/generals/photo'

/*
 * The five product mocks of How it works. Each is drawn at a fixed 840 × 438 and scaled to its
 * column by MockScale, so type and spacing keep their proportions at every width. On phones
 * MockScale draws them 420 wide instead, and the `@max-[600px]:` variants give each one a
 * narrow layout that fits without cutting anything off.
 */

export const STEP_MOCK_WIDTH = 840
export const STEP_MOCK_NARROW_WIDTH = 420

const CARD = 'rounded-[12px] border-[0.5px] border-ink/15 bg-[#f8f6f5] shadow-[0_1px_2px_rgb(10_13_18/0.05)]'

interface MockFrameProps {
    children: ReactNode
    className?: string
}

/** The frame chrome: translucent white, a hairline and 22px corners, concentric with the cards it holds. */
export const MockFrame: FC<MockFrameProps> = ({ children, className }): ReactNode => {
    return (
        <div aria-hidden='true' className={cn('reveal-group relative h-[438px] w-full rounded-[22px] border-[0.5px] border-ink/15 bg-white/40 p-3 @max-[600px]:h-auto', className)}>
            {children}
        </div>
    )
}

interface PointerProps {
    className: string
    order: number
    /** A small name tag riding with the pointer. */
    tag?: string
}

/** A pointer that glides in and settles over its target. */
export const Pointer: FC<PointerProps> = ({ className, order, tag }): ReactNode => {
    return (
        <span className={cn('pointer-glide pointer-events-none absolute z-30', className)} style={delay(order)}>
            <svg viewBox='0 0 20 24' className='h-[26px] w-[22px] drop-shadow-[0_2px_3px_rgb(40_38_34/0.3)]'>
                <path d='M2 2 L 2 19 L 6.6 14.8 L 9.8 21.6 L 12.8 20.2 L 9.7 13.6 L 16 13.4 Z' fill='#282622' stroke='#fffdf8' strokeWidth='1.4' strokeLinejoin='round' />
            </svg>
            {tag && (
                <span className='absolute left-4 top-[22px] whitespace-nowrap rounded-full border border-[#e8e4df] bg-ink px-2 py-0.5 text-[12px] font-medium leading-[18px] text-paper'>
                    {tag}
                </span>
            )}
        </span>
    )
}

/* 1. Role submission: a board of open roles ----------------------------------------- */

interface Job {
    title: string
    company: CompanyName
    location: string
    description: string
    meta: string
}

const JOBS: Job[] = [
    { title: 'Senior Product Designer', company: 'Halyard', location: 'Stockholm/London', description: 'AI-assisted spreadsheet tool looking for design systems expertise', meta: 'Hybrid • Full-time • $170k–$210k' },
    { title: 'Senior Visual Designer', company: 'Northfold', location: 'San Francisco/Remote', description: 'The identity platform for modern web teams, used by millions', meta: 'Remote • Full-time • $150k–$185k' },
    { title: 'Founding Designer', company: 'Tessel', location: 'Remote (US)', description: 'Collaborative onboarding for modern B2B software teams', meta: 'Remote • Full-time • $140k–$180k + equity' },
    { title: 'Senior Design Engineer', company: 'Brightline', location: 'Berlin/Remote', description: 'Realtime infrastructure for voice and video products', meta: 'Remote • Full-time • $160k–$200k' },
    { title: 'Staff Product Designer', company: 'Pendulum', location: 'New York/Remote', description: 'Workflows that help brands grow organic traffic', meta: 'Hybrid • Full-time • $180k–$220k' },
    { title: 'Founding Brand Designer', company: 'Kiln', location: 'New York', description: 'Autonomous platform engineering for the modern cloud', meta: 'On-site • Full-time • $150k–$190k + equity' }
]

export const JobBoardWindow: FC = (): ReactNode => {
    return (
        <MockFrame className='grid grid-cols-2 content-center gap-x-3 gap-y-2.5 @max-[600px]:grid-cols-1'>
            {JOBS.map((job, index) => (
                <div key={job.title} className={cn(index === 0 && 'click-pulse', index > 2 && '@max-[600px]:hidden')} style={index === 0 ? delay(2500) : undefined}>
                    <div className={cn(CARD, 'rise flex h-full flex-col gap-4 p-5', index === 0 && 'border-ink/35')} style={delay(100 + index * 140)}>
                        <div>
                            <div className='flex items-center justify-between gap-3'>
                                <p className='whitespace-nowrap text-[16px] font-medium leading-6 text-ink @max-[600px]:text-[18px] @max-[600px]:leading-7'>
                                    {job.title}
                                </p>
                                <Wordmark name={job.company} className='shrink-0 text-[14px] text-ink/85' />
                            </div>
                            <p className='text-[12px] font-medium leading-[18px] text-ink/80 @max-[600px]:text-[14px] @max-[600px]:leading-5'>
                                {job.company} • {job.location}
                            </p>
                        </div>

                        <div className='flex flex-col gap-1 text-[10px] font-medium leading-[14px] text-ink/60 @max-[600px]:text-[13px] @max-[600px]:leading-[18px]'>
                            <p>{job.description}</p>
                            <p>{job.meta}</p>
                        </div>
                    </div>
                </div>
            ))}

            <Pointer className='left-[30%] top-[18%]' order={1300} tag='Hiring Manager' />
        </MockFrame>
    )
}

/* 2. Shortlist: the scout's collective as a table -------------------------------------- */

interface Member {
    photo: PhotoId
    role: string
    focus: string
    tint: 'neutral' | 'blush'
}

const MEMBERS: Member[] = [
    { photo: 'luca', role: 'Principal Product Designer', focus: 'Design', tint: 'neutral' },
    { photo: 'mateo', role: 'Senior Design Engineer', focus: 'Design Engineering', tint: 'blush' },
    { photo: 'adrian', role: 'Senior Visual Designer', focus: 'Brand Design', tint: 'blush' },
    { photo: 'sofia', role: 'Staff Product Designer', focus: 'Design', tint: 'neutral' }
]

const COLUMNS = 'grid grid-cols-[350px_110px_206px_1fr] @max-[600px]:grid-cols-[1fr_auto]'

/* Cells that only fit the wide table */
const WIDE_ONLY = '@max-[600px]:hidden'

const BADGE = 'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[12px] font-medium leading-[18px]'

const BADGE_TINTS: Record<Member['tint'], string> = {
    neutral: 'border-[#d1c9be] bg-[#f8f6f5] text-ink',
    blush: 'border-azure/35 bg-cloud/35 text-ink'
}

interface CheckboxProps {
    checked?: boolean
    order?: number
}

const Checkbox: FC<CheckboxProps> = ({ checked = false, order = 0 }): ReactNode => {
    return (
        <span className='flex size-5 shrink-0 items-center justify-center rounded-[6px] border border-[#d5d7da] bg-white'>
            {checked && <Check className='pop size-3.5 text-ink' strokeWidth={2.5} style={delay(order)} />}
        </span>
    )
}

export const ShortlistWindow: FC = (): ReactNode => {
    return (
        <MockFrame className='p-0 @max-[600px]:p-3'>
            <div className='rise absolute inset-3 overflow-hidden rounded-[12px] @max-[600px]:static border border-[#e9e6e1] bg-white shadow-[0_1px_2px_rgb(10_13_18/0.05)]' style={delay(100)}>
                <div className='flex items-center gap-2 border-b border-[#e9e6e1] px-6 py-5'>
                    <p className='whitespace-nowrap font-serif text-[18px] font-light leading-7 text-ink'>
                        Maren's Collective
                    </p>
                    <span className={cn(BADGE, BADGE_TINTS.neutral)}>
                        4 profiles
                    </span>
                    <span className='ml-auto flex gap-[3px]'>
                        <i className='size-[3px] rounded-full bg-ink/40' />
                        <i className='size-[3px] rounded-full bg-ink/40' />
                        <i className='size-[3px] rounded-full bg-ink/40' />
                    </span>
                </div>

                <div className={cn(COLUMNS, 'border-b border-[#e9e6e1] text-[12px] font-semibold leading-[18px] text-ink')}>
                    <span className='flex h-11 items-center gap-3 px-6 @max-[600px]:px-4'>
                        <Checkbox />
                        Name
                    </span>
                    <span className='flex h-11 items-center px-6'>Status</span>
                    <span className={cn('flex h-11 items-center px-6', WIDE_ONLY)}>Role</span>
                    <span className={cn('flex h-11 items-center px-6', WIDE_ONLY)}>Specialisation</span>
                </div>

                {MEMBERS.map((member, index) => (
                    <div
                        key={member.role}
                        className={cn(COLUMNS, 'rise h-[72px] border-b border-[#e9e6e1] transition-colors last:border-b-0', index === 0 && 'bg-[#f8f6f5]')}
                        style={delay(420 + index * 160)}
                    >
                        <span className='flex items-center gap-3 px-6 @max-[600px]:px-4'>
                            <Checkbox checked={index === 0} order={2500} />
                            <Photo id={member.photo} sizes='40px' portrait className='size-10 shrink-0 rounded-full border border-ink/10 grayscale' />
                            <span className='whitespace-nowrap text-[14px] font-medium leading-5 text-ink'>
                                Member #{index + 1}
                            </span>
                        </span>
                        <span className='flex items-center px-6'>
                            <span className='inline-flex items-center gap-1 rounded-[6px] border border-[#d5d7da] bg-white px-1.5 py-0.5 text-[12px] font-medium leading-[18px] text-ink shadow-[0_1px_2px_rgb(10_13_18/0.05)]'>
                                <i className='size-2 rounded-full bg-[#7f9a6b]' />
                                Active
                            </span>
                        </span>
                        <span className={cn('flex items-center whitespace-nowrap px-6 text-[14px] leading-5 text-ink/75', WIDE_ONLY)}>
                            {member.role}
                        </span>
                        <span className={cn('flex items-center px-6', WIDE_ONLY)}>
                            <span className={cn(BADGE, BADGE_TINTS[member.tint])}>
                                {member.focus}
                            </span>
                        </span>
                    </div>
                ))}
            </div>

            <Pointer className='left-[5%] top-[36%] @max-[600px]:left-[7%]' order={1500} tag='Hiring Manager' />
        </MockFrame>
    )
}

/* 3. Review: candidate cards with the scout's take ---------------------------------- */

interface Candidate {
    name: string
    role: string
    location: string
    quote: string
    photo: PhotoId
}

const CANDIDATES: Candidate[] = [
    {
        name: 'Ada Lovelace',
        role: 'Principal Brand Designer',
        location: 'Based in Berlin. Will relocate to London. Eligible for the Global Talent Visa.',
        quote: 'Ada is a really strong candidate. Impressive body of work and massive value add',
        photo: 'sofia'
    },
    {
        name: 'Filip Elisë',
        role: 'Senior Product Designer',
        location: 'Currently in Amsterdam. Open to remote or hybrid roles across the EU and UK.',
        quote: 'Filip keeps impressing me. A long history of strong delivery and technical experiments',
        photo: 'mateo'
    }
]

const PILL = 'inline-flex items-center gap-1 whitespace-nowrap rounded-[7.5px] border-[0.5px] border-ink/10 py-[2.5px] pl-[7.5px] pr-2.5 text-[15px] font-medium leading-[23px]'

export const ReviewWindow: FC = (): ReactNode => {
    return (
        <MockFrame className='flex flex-col justify-center gap-5'>
            {CANDIDATES.map((candidate, index) => (
                <div key={candidate.name} className={cn(CARD, 'rise rounded-[10px] p-3')} style={delay(100 + index * 260)}>
                    <div className='flex flex-col gap-[15px] p-2.5'>
                        <div className='flex items-start gap-2.5 @max-[600px]:flex-wrap'>
                            <Photo id={candidate.photo} sizes='50px' portrait className='size-[50px] shrink-0 rounded-full border border-ink/10 grayscale' />
                            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                                <p className='whitespace-nowrap text-[17.5px] font-medium leading-[25px] text-ink'>
                                    {candidate.name}
                                </p>
                                <p className='whitespace-nowrap text-[17.5px] font-medium leading-[25px] text-ink/60 @max-[600px]:whitespace-normal'>
                                    {candidate.role} •{' '}
                                    <span className='underline decoration-ink/25 underline-offset-2'>LinkedIn</span>
                                    <span className='text-ink/30'> | </span>
                                    <span className='underline decoration-ink/25 underline-offset-2'>Portfolio</span>
                                </p>
                            </div>
                            <div className='flex shrink-0 gap-2 @max-[600px]:order-last @max-[600px]:w-full @max-[600px]:pl-[60px]'>
                                <span className={cn(PILL, 'bg-ink/5 text-ink')}>
                                    <X className='size-[15px]' strokeWidth={2} />
                                    Pass
                                </span>
                                <span
                                    className={cn(PILL, index === 0 ? 'click-pulse bg-ink text-paper' : 'bg-ink/5 text-ink')}
                                    style={index === 0 ? delay(2300) : undefined}
                                >
                                    <Check className='size-[15px]' strokeWidth={2} />
                                    Request intro
                                </span>
                            </div>
                        </div>

                        <p className='-mt-[11px] pl-[60px] text-[17.5px] font-medium leading-[25px] text-ink/60'>
                            {candidate.location}
                        </p>

                        <div className='rise flex h-[46px] items-center gap-2.5 rounded-[7.5px] bg-[rgb(232_228_223/0.35)] px-2' style={delay(600 + index * 260)}>
                            <Photo id='elena' sizes='28px' portrait className='size-7 shrink-0 rounded-full' />
                            <p className='truncate text-[17.5px] font-medium leading-[25px] text-ink/80'>
                                “{candidate.quote}”
                            </p>
                        </div>
                    </div>
                </div>
            ))}

            <Pointer className='left-[82%] top-[12%] @max-[600px]:hidden' order={1600} tag='Hiring Manager' />
        </MockFrame>
    )
}

/* 4. Intros: the scout's introduction email ----------------------------------------- */

const MAIL_PILL = 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-ink/15 bg-[#fdfcfa] px-3.5 py-1 text-[15px] font-medium leading-[23px] text-ink'

export const IntroWindow: FC = (): ReactNode => {
    return (
        <MockFrame className='flex flex-col justify-center'>
            <div className={cn(CARD, 'flex min-h-[414px] flex-col justify-center rounded-[10px] p-3 @max-[600px]:min-h-0')}>
                <div className='flex flex-col gap-3 p-2.5'>
                    <p className='rise text-[21.5px] font-semibold leading-7 text-ink' style={delay(100)}>
                        Intro: Ada {'<>'} Marta
                    </p>

                    <div className='rise flex items-start gap-2.5 @max-[600px]:flex-wrap' style={delay(260)}>
                        <Photo id='elena' sizes='40px' portrait className='size-10 shrink-0 rounded-full' />
                        <div className='flex min-w-0 flex-1 flex-col gap-px'>
                            <p className='whitespace-nowrap text-[17.5px] font-medium leading-6 text-ink/85'>
                                Maren Holt <span className='text-[15px] font-normal text-ink/45'>Kerning scout</span>
                            </p>
                            <p className='text-[15px] font-medium leading-5 text-ink/50'>
                                to Marta, Ada
                            </p>
                        </div>
                        <p className='whitespace-nowrap text-[17.5px] font-semibold leading-[25px] text-ink @max-[600px]:w-full @max-[600px]:pl-[50px] @max-[600px]:text-[15px] @max-[600px]:text-ink/60'>
                            Friday, 10 October
                        </p>
                    </div>

                    <div className='rise flex flex-col gap-[11px] text-[17.5px] font-medium leading-[25px] text-ink/60' style={delay(480)}>
                        <p>
                            Hey both,
                            <br />
                            Excited to make this connection. You both have all the context you need.
                        </p>
                        <ul className='list-disc pl-[26px]'>
                            <li>Marta: I'll let you take it from here</li>
                            <li>Ada: if you are in other processes, let Marta know how far along you are</li>
                        </ul>
                        <p>
                            Happy connecting.
                            <br />
                            P.S. Feel free to keep me in cc. It helps us stay close.
                        </p>
                    </div>

                    <div className='rise flex gap-2' style={delay(760)}>
                        <span className={MAIL_PILL}>
                            <Reply className='size-4' strokeWidth={1.75} />
                            Reply
                        </span>
                        <span className={MAIL_PILL}>
                            <Forward className='size-4' strokeWidth={1.75} />
                            Forward
                        </span>
                    </div>
                </div>
            </div>
        </MockFrame>
    )
}

/* 5. Offer: the shared channel the day it lands --------------------------------------- */

interface ReactionProps {
    icon: ReactNode
    count: number
    mine?: boolean
    order: number
}

const Reaction: FC<ReactionProps> = ({ icon, count, mine = false, order }): ReactNode => {
    return (
        <span
            className={cn(
                'pop inline-flex items-center gap-1 rounded-full border px-2 py-[2px] text-[11.5px] font-semibold',
                mine ? 'border-clay/40 bg-cloud/30 text-clay' : 'border-ink/15 bg-white text-ink/70'
            )}
            style={delay(order)}
        >
            {icon}
            {count}
        </span>
    )
}

export const OfferWindow: FC = (): ReactNode => {
    return (
        <MockFrame className='flex flex-col justify-center'>
            <div className={cn(CARD, 'rise relative flex h-[414px] flex-col overflow-hidden rounded-[8px] bg-white p-0 @max-[600px]:h-auto')} style={delay(100)}>
                <div className='flex h-11 shrink-0 items-center gap-2.5 px-3.5 text-ink/60'>
                    <span className='flex items-center gap-1 text-[15px] font-bold text-ink'>
                        <Hash className='size-4' strokeWidth={2.25} />
                        ext-kerning-halyard
                    </span>
                    <span className='ml-auto flex items-center gap-1 rounded-[7px] border border-ink/15 px-2 py-1 text-[12px] font-medium'>
                        <span className='flex -space-x-1.5'>
                            <Photo id='nora' sizes='16px' portrait className='size-4 rounded-full ring-1 ring-white' />
                            <Photo id='elena' sizes='16px' portrait className='size-4 rounded-full ring-1 ring-white' />
                        </span>
                        6
                    </span>
                </div>

                <div className='flex h-[33px] shrink-0 items-center gap-[18px] border-b border-ink/10 px-4 text-[12.5px] text-ink/60'>
                    <span className='relative flex h-full items-center font-bold text-ink after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-clay'>
                        Messages
                    </span>
                    <span>Files and links</span>
                </div>

                <div className='flex min-h-0 flex-1 flex-col justify-end gap-2 px-4 pb-3.5 pt-2.5'>
                    <div className='mb-auto mt-1.5'>
                        <p className='flex items-center gap-1.5 text-[17px] font-extrabold text-ink'>
                            <Hash className='size-4' strokeWidth={2.5} />
                            ext-kerning-halyard
                        </p>
                        <p className='mt-1 text-[12.5px] leading-[17px] text-ink/80'>
                            <span className='rounded-[3px] bg-cloud/40 px-0.5 font-medium text-clay'>@Kerning</span> created this channel on 2 September. This is the very beginning of the channel.
                        </p>
                    </div>

                    <div className='relative mb-1.5 text-center before:absolute before:-inset-x-4 before:top-1/2 before:h-px before:bg-ink/10'>
                        <span className='relative inline-flex rounded-full border border-ink/10 bg-white px-3 py-1 text-[11.5px] font-bold text-ink'>
                            Monday, 13 October
                        </span>
                    </div>

                    <div className='pop flex items-start gap-2.5' style={delay(700)}>
                        <span className='flex size-[34px] shrink-0 items-center justify-center rounded-[7px] bg-ink text-paper'>
                            <LogoMark className='size-[18px]' />
                        </span>
                        <div className='flex min-w-0 flex-col gap-1'>
                            <p className='text-[13.5px] font-bold text-ink'>
                                Kerning <span className='ml-1 text-[11px] font-normal text-ink/50'>9:41</span>
                            </p>
                            <p className='text-[13.5px] leading-[19px] text-ink'>
                                Signed and sealed. Ada has accepted the offer and starts on 3 November. Thank you for trusting the process.
                            </p>
                            <span className='mt-1 inline-flex w-fit items-center gap-2.5 rounded-[9px] border border-ink/10 bg-white py-1.5 pl-2 pr-3.5'>
                                <span className='flex h-8 w-7 items-center justify-center rounded-[5px] bg-clay text-paper'>
                                    <FileText className='size-4' strokeWidth={1.75} />
                                </span>
                                <span>
                                    <b className='block text-[12.5px] font-bold text-ink'>Ada Lovelace, signed offer.pdf</b>
                                    <small className='mt-px block text-[10.5px] text-ink/55'>PDF</small>
                                </span>
                            </span>
                            <span className='mt-0.5 flex gap-1.5'>
                                <Reaction icon={<PartyPopper className='size-3' strokeWidth={2} />} count={4} order={1300} />
                                <Reaction icon={<Heart className='size-3' strokeWidth={2} />} count={2} order={1450} mine />
                            </span>
                        </div>
                    </div>

                    <div className='pop flex items-start gap-2.5' style={delay(2000)}>
                        <Photo id='nora' sizes='34px' portrait className='size-[34px] shrink-0 rounded-[7px]' />
                        <div className='flex min-w-0 flex-col gap-1'>
                            <p className='text-[13.5px] font-bold text-ink'>
                                Marta Lindqvist <span className='ml-1 text-[11px] font-normal text-ink/50'>9:46</span>
                            </p>
                            <p className='text-[13.5px] leading-[19px] text-ink'>
                                Could not have asked for a better finish. You were with us right to the end.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </MockFrame>
    )
}
